import { memo, useCallback, useEffect } from 'react';

import { isSessionSentFirstTimeAtom, sessionAtom, useAtom } from '@/store';

import { AppEvents } from '@/utils';

import { getGeolocation, getSessionData, updateSessionShopify } from '@/modules/api';
import * as types from '@/modules/api/types';
import { useEvent } from '@/modules/common';

const CHECKOUT_BUTTON_SELECTOR = '.cart-footer__checkout-button';

export const SessionSyncHandler = memo(() => {
  const [session, setSession] = useAtom(sessionAtom);
  const [isSessionSentFirstTime, setIsSessionSentFirstTime] = useAtom(isSessionSentFirstTimeAtom);

  const { location, userAgent } = session;

  const sendSessionInfo = useCallback(
    async (updateSession?: types.SessionData) => {
      await updateSessionShopify(updateSession);
      if (!isSessionSentFirstTime) {
        setIsSessionSentFirstTime(true);
      }
    },
    [isSessionSentFirstTime, setIsSessionSentFirstTime]
  );

  useEvent({ name: AppEvents.UPDATE_SESSION, handler: () => sendSessionInfo() });

  useEffect(() => {
    if (!session.userAgent) {
      return;
    }
    sendSessionInfo(session);

    // TODO: Refactor this to use ApiApp
    const handleClick = async (event: MouseEvent) => {
      const $target = event.target as HTMLElement;
      const $button = $target.closest<HTMLAnchorElement>(CHECKOUT_BUTTON_SELECTOR);

      if ($button) {
        // await sendSessionInfo();

        if ($button.href) {
          window.location.href = $button.href;
        }
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [session, sendSessionInfo]);

  useEffect(() => {
    if (userAgent) {
      return;
    }

    (async () => {
      const session = await getSessionData();
      setSession(session);
    })();
  }, [userAgent, setSession]);

  useEffect(() => {
    if (location) {
      return;
    }

    (async () => {
      let ipLocation = null;
      try {
        const { country_code } = await getGeolocation();
        ipLocation = country_code;
      } catch (error) {
        console.error('Failed to fetch geolocation:', error);
      }

      if (ipLocation) {
        setSession((prev) => ({
          ...prev,
          location: ipLocation,
        }));
      }
    })();
  }, [location, setSession]);

  return null;
});

SessionSyncHandler.displayName = 'SessionSyncHandler';
